import { Tabs } from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConsentReportsList from "../ConsentReportsList/ConsentReportsList";
import Navbar from "../../../../components/Navbar/Navbar";
import { useDebounce } from "../../../../hooks/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    useGetCollateralConsentReports,
    useGetConsentReports,
    useGetExpiringConsents,
} from "../../hooks/queries/reports.queries";
import { ConsentStatus } from "../../types/reports.types";
import ExpiringConsentsReportsList from "../ConsentReportsList/ExpiringConsentsReportsList";
import AttendeeConsentReportsList from "../AttendeeConsentReportsList/AttendeeConsentReportsList";

export default function ConsentReportWrapper() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const searchString = searchParams.get("search") || "";

    const clientFilter = searchParams.get("client_id") || "";
    const providerFilter = searchParams.get("provider_id") || "";
    const startDateFromFilter = searchParams.get("start_date_from");
    const startDateToFilter = searchParams.get("start_date_to");
    const statusFilter = searchParams.get("status") || "";
    const sessionStatusFilter = searchParams.get("session_status") || "";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const offsetFilter = (pageFilter - 1) * limitFilter;

    const search = useDebounce(searchString, 500);

    const { data, error, isLoading } = useGetConsentReports({
        search_string: search,
        client_id: clientFilter,
        provider_id: providerFilter,
        status: statusFilter as ConsentStatus,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
        session_status: sessionStatusFilter,
        page: pageFilter,
        limit: limitFilter,
    });

    const {
        data: expiringConsentsData,
        error: expiringConsentsError,
        isLoading: expiringConsentsLoading,
    } = useGetExpiringConsents({});

    const {
        data: collateralConsentsData,
        error: collateralConsentsError,
        isLoading: collateralConsentsLoading,
    } = useGetCollateralConsentReports({
        search_string: search,
        status: statusFilter as ConsentStatus,
        start_date_from: startDateFromFilter || undefined,
        start_date_to: startDateToFilter || undefined,
        limit: limitFilter,
        offset: offsetFilter,
    });

    const activeTab = searchParams.get("tab") || "all";
    const handleSetTabInUrl = (tab: string) => {
        navigate(`/client-forms/consent-form-reports?tab=${tab}`);
    };

    let consentReportsCount;
    if (error) consentReportsCount = "";
    else if (isLoading) consentReportsCount = "";
    else if (data?.data && data?.data.length === 0) consentReportsCount = 0;
    else if (data?.data && data?.data.length > 0)
        consentReportsCount = data.total_count;

    let expiringConsentsCount;
    if (expiringConsentsError) expiringConsentsCount = "";
    else if (expiringConsentsLoading) expiringConsentsCount = "";
    else if (
        expiringConsentsData?.data &&
        expiringConsentsData?.data.length === 0
    )
        expiringConsentsCount = 0;
    else if (
        expiringConsentsData?.data &&
        expiringConsentsData?.data.length > 0
    )
        expiringConsentsCount = expiringConsentsData.total_count;

    let collateralConsentsCount;
    if (collateralConsentsError) collateralConsentsCount = "";
    else if (collateralConsentsLoading) collateralConsentsCount = "";
    else if (
        collateralConsentsData?.data &&
        collateralConsentsData?.data.length === 0
    )
        collateralConsentsCount = 0;
    else if (
        collateralConsentsData?.data &&
        collateralConsentsData?.data.length > 0
    )
        collateralConsentsCount = collateralConsentsData.total_count;

    const tabItems = [
        {
            key: "all",
            label: "All Consent Reports",
            children: <ConsentReportsList />,
            count: consentReportsCount,
            onClick: () => handleSetTabInUrl("all"),
        },
        {
            key: "expiring",
            label: "Expiring Consent Reports",
            children: <ExpiringConsentsReportsList />,
            count: expiringConsentsCount,
            onClick: () => handleSetTabInUrl("expiring"),
        },
        {
            key: "collateral",
            label: "Attendee Consent",
            children: <AttendeeConsentReportsList />,
            count: collateralConsentsCount,
            onClick: () => handleSetTabInUrl("collateral"),
        },
    ];

    return (
        <>
            <Navbar title="Consent Reports" />
            <div className="page">
                <Tabs items={tabItems} defaultActiveKey={activeTab} />
            </div>
        </>
    );
}
